import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Car,
  User as UserIcon,
  CheckCircle,
  Warning,
  ArrowSquareOut,
  Calendar,
  Money,
  Receipt,
} from "@phosphor-icons/react";
import api, { API } from "../lib/api";

/**
 * Public payment receipt page — /recibo/:token
 * No auth required; the URL token IS the credential (90-day TTL).
 * The client lands here from a WhatsApp/SMS link the owner sent them.
 */
export default function ReceiptPage() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/public/receipt/${token}`);
        if (!cancelled) setState({ loading: false, data, error: null });
      } catch (err) {
        const status = err.response?.status;
        if (!cancelled) setState({
          loading: false, data: null,
          error: {
            status,
            message:
              status === 410 ? "Este recibo ha expirado." :
              status === 404 ? "Enlace inválido o eliminado." :
              err.response?.data?.detail || "No se pudo cargar el recibo.",
          },
        });
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  if (state.loading) {
    return (
      <FullScreen>
        <div className="text-white/60 font-mono text-sm">Cargando recibo...</div>
      </FullScreen>
    );
  }

  if (state.error) {
    return (
      <FullScreen>
        <div className="max-w-md rounded-2xl border border-red-500/40 bg-red-500/10 p-6 text-center">
          <Warning size={36} weight="fill" className="mx-auto mb-2 text-red-400" />
          <div className="text-lg font-heading font-bold text-white mb-1">Recibo no disponible</div>
          <div className="text-[12px] font-mono text-white/60">{state.error.message}</div>
          <div className="text-[11px] font-mono text-white/55 mt-3">
            Pídele al vendedor que te envíe un nuevo enlace.
          </div>
        </div>
      </FullScreen>
    );
  }

  const { data } = state;
  const r = data.receivable;
  const p = data.payment;
  const b = data.balance;
  const vehicle = `${r.vehicle_year || ""} ${r.vehicle_make || ""} ${r.vehicle_model || ""}`.trim() || "—";
  const pdfUrl = `${API}/public/receipt/${token}/pdf`;

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-[#0B0C13] via-[#0F1017] to-[#1A1B26] pb-16">
      <header className="border-b border-white/8 bg-black/40 backdrop-blur-sm px-4 py-3 sticky top-0 z-30">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded bg-emerald-500 flex items-center justify-center">
              <Receipt size={16} weight="fill" className="text-slate-900" />
            </div>
            <div>
              <div className="text-[9px] font-mono uppercase tracking-widest text-emerald-300">
                RECIBO DE PAGO
              </div>
              <div className="text-[11px] font-heading font-bold text-white leading-tight">
                Comprobante oficial
              </div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9px] font-mono font-bold tracking-widest">
            <CheckCircle size={10} weight="fill" /> PAGADO
          </span>
        </div>
      </header>

      <main className="max-w-2xl mx-auto p-4 space-y-4">
        {/* Big amount card */}
        <section
          data-testid="receipt-amount"
          className="rounded-2xl border border-emerald-500/40 bg-emerald-500/8 p-6 text-center"
        >
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-300 mb-1">
            Monto recibido
          </div>
          <div className="text-5xl sm:text-6xl font-heading font-bold text-white tabular-nums my-2">
            {money(p.received_amount)}
          </div>
          <div className="text-[11px] font-mono text-white/60">
            {p.method || "—"}{p.reference ? ` · Ref. ${p.reference}` : ""}
          </div>
        </section>

        {/* Meta */}
        <section
          data-testid="receipt-meta"
          className="rounded-2xl border border-white/10 bg-black/30 p-4 grid grid-cols-2 gap-4"
        >
          <KV icon={<UserIcon size={11} weight="bold" />} label="Cliente" value={r.client_name || "—"} />
          <KV icon={<Calendar size={11} weight="bold" />} label="Fecha del pago" value={p.payment_date || "—"} />
          <KV icon={<Car size={11} weight="bold" />} label="Vehículo" value={vehicle} full />
          {p.extra_charge > 0 && (
            <KV icon={<Money size={11} weight="bold" />} label="Cargo adicional" value={money(p.extra_charge)} accent="amber" full />
          )}
          {p.notes && (
            <div className="col-span-2">
              <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-white/40">Notas</div>
              <div className="text-white/80 text-[13px] mt-0.5 whitespace-pre-wrap break-words">{p.notes}</div>
            </div>
          )}
        </section>

        {/* Balance */}
        <section
          data-testid="receipt-balance"
          className="rounded-2xl border border-white/10 bg-black/30 p-4"
        >
          <div className="text-[9px] font-mono uppercase tracking-widest text-white/40 mb-3">
            Estado del contrato
          </div>
          <div className="grid grid-cols-3 gap-3">
            <BalanceCell label="Total contrato" value={money(b.total_contract)} />
            <BalanceCell label="Cobrado" value={money(b.total_collected)} accent="emerald" />
            <BalanceCell label="Saldo pendiente" value={money(b.current_balance)} accent="amber" />
          </div>
          <div className="mt-3 h-1.5 w-full bg-white/6 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full transition-all"
              style={{ width: `${Math.min(100, Math.max(0, b.pct_paid || 0))}%` }}
            />
          </div>
          <div className="text-[10px] font-mono text-white/40 mt-1 tabular-nums text-right">
            {(b.pct_paid || 0).toFixed(1)}% pagado
          </div>
        </section>

        <a
          data-testid="receipt-download-pdf"
          href={pdfUrl}
          target="_blank"
          rel="noreferrer"
          className="on-solid w-full flex items-center justify-center gap-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-heading font-bold py-3 text-sm"
        >
          <ArrowSquareOut size={16} weight="bold" />
          Descargar recibo en PDF
        </a>

        <div className="text-center text-[10px] font-mono text-white/50 pt-2">
          Este comprobante expira {(data.expires_at || "").slice(0, 10)} · Guárdalo como constancia de tu pago.
        </div>
      </main>
    </div>
  );
}

function money(v) {
  if (v == null) return "—";
  return `$${Number(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function FullScreen({ children }) {
  return <div className="min-h-screen w-full bg-[#0F1017] flex items-center justify-center p-4">{children}</div>;
}

function KV({ icon, label, value, accent, full }) {
  const color = accent === "amber" ? "text-amber-300" : accent === "emerald" ? "text-emerald-300" : "text-white";
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-white/40">
        {icon} {label}
      </div>
      <div className={`text-[14px] font-heading font-bold ${color} mt-0.5 break-words`}>{value}</div>
    </div>
  );
}

function BalanceCell({ label, value, accent }) {
  const color = accent === "amber" ? "text-amber-300" : accent === "emerald" ? "text-emerald-300" : "text-white";
  return (
    <div>
      <div className="text-[8px] font-mono uppercase tracking-widest text-white/40 mb-0.5">{label}</div>
      <div className={`text-[15px] font-heading font-bold ${color} tabular-nums break-words`}>{value}</div>
    </div>
  );
}
